import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

type LocaleMap = typeof localeToCurrencyMap;

const localeToCurrencyMap = {
	en: "EUR",
	de: "EUR",
};

export const getCurrencyByLocale = (locale: string) => {
	if (locale in localeToCurrencyMap) {
		return localeToCurrencyMap[locale as keyof LocaleMap];
	}
	return localeToCurrencyMap.de; // Default to EUR if locale is not found
};

export const useCurrency = () => {
	const { i18n } = useTranslation();

	return getCurrencyByLocale(i18n.language);
};

export const getCurrencyPrice = (price: number, locale = "de") => {
	const currency = getCurrencyByLocale(locale);

	return Intl.NumberFormat(locale, {
		style: "currency",
		currency,
	}).format(price / 100);
};

/**
 * Formats a price in cents to a locale-aware number string without currency symbol.
 * Drops decimal places for round amounts (e.g., "50" instead of "50,00").
 *
 * @param cents - Price in cents
 * @param locale - Locale string (e.g., "de", "en")
 * @returns Formatted number string without currency symbol (e.g., "50" or "49,99")
 */
export const formatCentsAsAmount = (cents: number, locale = "de"): string => {
	const major = cents / 100;
	const fractionDigits = Number.isInteger(major) ? 0 : 2;
	return new Intl.NumberFormat(locale, {
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits,
	}).format(major);
};

export function useCurrencyPrice(initialPrice = 0) {
	const { i18n } = useTranslation();

	const getPrice = useCallback(
		(price: number) => getCurrencyPrice(price, i18n.language),
		[i18n.language],
	);

	const currencyPrice = useMemo(() => {
		return getPrice(initialPrice);
	}, [initialPrice, getPrice]);

	return { currencyPrice, getCurrencyPrice: getPrice };
}
